import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { alpha, Box, BoxProps, Paper, Theme, SxProps, useTheme } from '@mui/material';
import { decomposeColor, lighten, recomposeColor } from '@mui/material/styles';
import { CanvasPosition } from '../types';

const KEYBOARD_GRID_SIZE = 30;
const KEYBOARD_GRID_OFFSET = 100000;

const CanvasCardHighlightContext = React.createContext(false);

/** Selected or actively dragged — matches CanvasCardContent highlight styling. */
export function useCanvasCardHighlighted(): boolean {
	return useContext(CanvasCardHighlightContext);
}

/** Default card shell / pill background (contentShell="default"). */
export function getCanvasCardSurfaceBackground(
	theme: Theme,
	options: { isHighlighted: boolean; collectionColor?: string },
): string {
	if (options.isHighlighted) {
		return alpha(theme.palette.primary.main, 0.15);
	}
	if (options.collectionColor) {
		return alpha(options.collectionColor, 0.025);
	}
	return 'transparent';
}

/** Blends `tint` into `base` at `ratio` (0-1) and recomposes to a fully opaque color — unlike `alpha`, nothing behind the card can show through the result. */
export function mixSolidColor(base: string, tint: string, ratio: number): string {
	const baseRgb = decomposeColor(base).values;
	const tintRgb = decomposeColor(tint).values;
	const mixed = baseRgb
		.slice(0, 3)
		.map((channel: number, i: number) => Math.round(channel * (1 - ratio) + tintRgb[i] * ratio)) as [number, number, number];
	return recomposeColor({ type: 'rgb', values: mixed });
}

/** Solid alternative to `getCanvasCardSurfaceBackground`, for cards whose background must stay opaque (e.g. no backdrop blur underneath to let a translucent tint sit on). */
export function getCanvasCardSolidSurfaceBackground(theme: Theme, options: { isHighlighted?: boolean; collectionColor?: string }): string {
	if (options.isHighlighted) return mixSolidColor(theme.palette.background.default, theme.palette.primary.main, 0.15);
	if (!options.collectionColor) return theme.palette.background.default;
	return mixSolidColor(theme.palette.background.default, options.collectionColor, 0.03);
}

/** Opaque, dimmed accent fill for the query card count block. */
export function getCanvasCardAccentBackground(
	theme: Theme,
	options: { isHighlighted: boolean; collectionColor?: string },
): string {
	if (options.isHighlighted) {
		return lighten(theme.palette.primary.main, 0.55);
	}
	if (options.collectionColor) {
		return lighten(options.collectionColor, 0.68);
	}
	const neutral = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[500];
	return lighten(neutral, 0.65);
}

/** Text on the query count circle. */
export function getCanvasCardAccentForeground(
	theme: Theme,
	options: { isHighlighted: boolean; collectionColor?: string },
): string {
	return theme.palette.getContrastText(getCanvasCardAccentBackground(theme, options));
}

export type CanvasCardContentProps = {
	children: React.ReactNode;
	/** When true, uses the wider minimum card width (220px). */
	wideMinWidth?: boolean;
	collectionColor?: string;
	isHighlighted?: boolean;
	sx?: SxProps<Theme>;
} & Omit<BoxProps, 'children' | 'sx'>;

/** Visual shell for a canvas card (Paper by default). */
export const CanvasCardContent = React.forwardRef<HTMLDivElement, CanvasCardContentProps>(
	function CanvasCardContent(
		{
			children,
			wideMinWidth,
			collectionColor,
			isHighlighted,
			sx,
			...boxProps
		},
		ref,
	) {
		const theme = useTheme();
		return (
			<Box
				ref={ref}
				component={Paper}
				sx={[
					{
						borderRadius: 2,
						width: 'max-content',
						maxWidth: 220,
						padding: 1.25,
						px: 2,
						position: 'relative',
						boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
						minWidth: wideMinWidth ? 220 : 120,
						// Solid rather than translucent so the canvas grid/other cards never show through — the
						// theme's default MuiPaper backdrop-filter blur is disabled for the same reason it'd
						// otherwise composite (expensive at the card counts a canvas can reach) with no visible effect.
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						backgroundColor: getCanvasCardSolidSurfaceBackground(theme, { isHighlighted: Boolean(isHighlighted), collectionColor }),
						borderColor: isHighlighted ? 'primary.main' : undefined,
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : []),
				]}
				{...boxProps}
			>
				{children}
			</Box>
		);
	},
);

export type CanvasCardPointerSelectHandler = (e: React.PointerEvent<Element>) => void;

/** Props shared by canvas cards that use world-space drag and the centered translate shell. */
export type CanvasCardDragSharedProps = {
	position: CanvasPosition;
	scale: number;
	isSelected: boolean;
	collectionColor?: string;
	/**
	 * When locked, pointer drags do not reposition the card or fire drag-activate/move callbacks
	 * (the drag pans the canvas instead); tap-to-select / focus still work.
	 */
	isPositionLocked?: boolean;
	onPositionChange: (x: number, y: number) => void;
	constrainPosition?: (x: number, y: number) => CanvasPosition;
	/** Rotation (deg) applied to the card while it's being dragged at the given position. */
	getDragTilt?: (x: number, y: number) => number;
	onDragStart?: () => void;
	onDragActivate?: () => void;
	onDragMove?: (x: number, y: number) => void;
	onDragEnd?: () => void;
	cardNodeRef?: (el: HTMLDivElement | null) => void;
	onSelect: CanvasCardPointerSelectHandler;
	onFocus: () => void;
	onBlur: () => void;
};

/** `default` — Paper shell via CanvasCardContent; `none` — drag/focus only, children supply visuals. */
export type CanvasCardContentShell = 'default' | 'none';

export type CanvasCardFrameProps = CanvasCardDragSharedProps & {
	ariaLabel: string;
	children: React.ReactNode;
	maxWidth?: boolean;
	/** When `'none'`, skips CanvasCardContent (no Paper, padding, or collection fill). */
	contentShell?: CanvasCardContentShell;
	sx?: SxProps<Theme>;
	/** Rendered inside the outer positioned Box but outside the card content shell. Moves with the card automatically. */
	decorations?: React.ReactNode;
	/** Overrides the default z-index (1). */
	zIndex?: number;
};

/**
 * Outer translate + inner Paper with pointer drag / tap-to-select.
 * Keeps keyboard tab order derived from world position (same grid as note/query cards).
 */
export function CanvasCardFrame({
	position,
	scale,
	isSelected,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	getDragTilt,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	ariaLabel,
	children,
	maxWidth,
	contentShell = 'default',
	sx,
	decorations,
	zIndex,
}: CanvasCardFrameProps) {
	const [dragStart, setDragStart] = useState<{
		x: number;
		y: number;
		screenX: number;
		screenY: number;
	} | null>(null);
	const [localPosition, setLocalPosition] = useState(position);
	const hasMovedRef = useRef(false);
	const hasActivatedDragRef = useRef(false);

	useEffect(() => {
		setLocalPosition(position);
	}, [position.x, position.y]);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent) => {
			if ((e.target as HTMLElement).closest('button')) return;
			e.preventDefault();
			hasMovedRef.current = false;
			hasActivatedDragRef.current = false;
			onDragStart?.();
			setDragStart({
				x: localPosition.x,
				y: localPosition.y,
				screenX: e.clientX,
				screenY: e.clientY,
			});
			(e.target as HTMLElement).setPointerCapture?.(e.pointerId);
		},
		[localPosition, onDragStart]
	);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!dragStart) return;
			// Locked cards still accept pointer-down/up (so tap-to-select keeps working),
			// but pointer movement never promotes the gesture into a drag.
			if (isPositionLocked) return;
			const dx = (e.clientX - dragStart.screenX) / scale;
			const dy = (e.clientY - dragStart.screenY) / scale;
			if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
				hasMovedRef.current = true;
				if (!hasActivatedDragRef.current) {
					hasActivatedDragRef.current = true;
					onDragActivate?.();
				}
			}
			const rawX = dragStart.x + dx;
			const rawY = dragStart.y + dy;
			const nextPosition = constrainPosition
				? constrainPosition(rawX, rawY)
				: { x: rawX, y: rawY };
			setLocalPosition(nextPosition);
			if (hasMovedRef.current) {
				onDragMove?.(nextPosition.x, nextPosition.y);
			}
		},
		[constrainPosition, dragStart, isPositionLocked, onDragActivate, onDragMove, scale]
	);

	const handlePointerUp = useCallback(
		(e: React.PointerEvent) => {
			(e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
			if (dragStart) {
				if (hasMovedRef.current) {
					onPositionChange(localPosition.x, localPosition.y);
				} else if (
					Math.abs((e.clientX - dragStart.screenX) / scale) <= 4
					&& Math.abs((e.clientY - dragStart.screenY) / scale) <= 4
				) {
					onSelect(e);
				}
				setDragStart(null);
				onDragEnd?.();
			}
		},
		[dragStart, localPosition, onDragEnd, onPositionChange, onSelect, scale]
	);

	const displayX = dragStart ? localPosition.x : position.x;
	const displayY = dragStart ? localPosition.y : position.y;
	const dragTilt = dragStart && hasMovedRef.current && getDragTilt ? getDragTilt(displayX, displayY) : 0;

	const row = Math.round(displayY / KEYBOARD_GRID_SIZE) + KEYBOARD_GRID_OFFSET;
	const col = Math.round(displayX / KEYBOARD_GRID_SIZE) + KEYBOARD_GRID_OFFSET;
	const tabIndex = row * 1000 + col;
	const isHighlighted = Boolean(dragStart) || isSelected;

	const pointerHandlers = {
		onPointerDown: handlePointerDown,
		onPointerMove: handlePointerMove,
		onPointerUp: handlePointerUp,
		onPointerLeave: handlePointerUp,
	};

	return (
		<Box
			ref={cardNodeRef}
			sx={{
				position: 'absolute',
				left: '50%',
				top: '50%',
				pointerEvents: 'auto',
				width: 'max-content',
				cursor: isPositionLocked ? 'pointer' : dragStart ? 'grabbing' : 'grab',
				userSelect: 'none',
				touchAction: 'none',
				zIndex: zIndex ?? 1,
			}}
			{...pointerHandlers}
			style={{
				transform: `translate(calc(-50% + ${displayX}px), calc(-50% + ${displayY}px)) rotate(${dragTilt}deg)`,
			}}
		>
			<CanvasCardHighlightContext.Provider value={isHighlighted}>
				{contentShell === 'none' ? (
					<Box sx={sx}>
						{children}
					</Box>
				) : (
					<CanvasCardContent
						collectionColor={collectionColor}
						isHighlighted={isHighlighted}
						wideMinWidth={maxWidth}
						sx={
							[
								sx,
							] as SxProps<Theme>
						}
					>
						{children}
					</CanvasCardContent>
				)}
			</CanvasCardHighlightContext.Provider>
			{decorations}
			<button
				type="button"
				tabIndex={tabIndex}
				aria-label={ariaLabel}
				onFocus={onFocus}
				onBlur={onBlur}
				style={{
					position: 'absolute',
					inset: 0,
					opacity: 0,
					pointerEvents: 'none',
				}}
			/>
		</Box>
	);
}

export default CanvasCardFrame;
